import React from "react"
import { Handle, Position } from "reactflow"
import { Inbox, Sparkles, GitBranch, Database, StickyNote } from "lucide-react"

import { getToolById } from "../registry"

/**
 * Custom reactflow node renderers for the AI Tooling flow canvas. One per node
 * `type`. Styling lives in `flow-canvas.scss` keyed off `.ai-flow-node--{type}`.
 *
 * Handles are vertical (target on top / source on bottom) so flows read
 * top-to-bottom. With reactflow's default `connectOnClick`, a user connects two
 * nodes by clicking one node's bottom handle then another's top handle. Nodes
 * never require a connection — they can sit on the board on their own.
 */

const TriggerNode = ({ data }) => (
	<div className="ai-flow-node ai-flow-node--trigger">
		<div className="ai-flow-node-icon">
			<Inbox size={15} />
		</div>
		<div className="ai-flow-node-text">
			<span className="ai-flow-node-title">{data.label}</span>
			<span className="ai-flow-node-kind">Trigger</span>
		</div>
		<Handle type="source" position={Position.Bottom} />
	</div>
)

const AgentNode = ({ data }) => {
	const tool = data.binding === "tool" ? getToolById(data.toolId) : null
	const model = tool?.defaults?.model || data.draft?.model || "-"
	const planned = data.binding === "placeholder" || data.status === "planned"

	return (
		<div
			className={[
				"ai-flow-node",
				"ai-flow-node--agent",
				planned && "is-planned",
			]
				.filter(Boolean)
				.join(" ")}>
			<Handle type="target" position={Position.Top} />
			<div className="ai-flow-node-icon">
				<Sparkles size={15} />
			</div>
			<div className="ai-flow-node-text">
				<span className="ai-flow-node-title">{data.label}</span>
				<span className="ai-flow-node-model">{model}</span>
			</div>
			<Handle type="source" position={Position.Bottom} />
		</div>
	)
}

const RouterNode = ({ data }) => (
	<div className="ai-flow-node ai-flow-node--router">
		<Handle type="target" position={Position.Top} />
		<div className="ai-flow-node-icon">
			<GitBranch size={15} />
		</div>
		<div className="ai-flow-node-text">
			<span className="ai-flow-node-title">{data.label}</span>
			<span className="ai-flow-node-kind">Router</span>
		</div>
		<Handle type="source" position={Position.Bottom} />
	</div>
)

const StepNode = ({ data }) => (
	<div className="ai-flow-node ai-flow-node--step">
		<Handle type="target" position={Position.Top} />
		<div className="ai-flow-node-icon">
			<Database size={15} />
		</div>
		<div className="ai-flow-node-text">
			<span className="ai-flow-node-title">{data.label}</span>
			<span className="ai-flow-node-kind">Deterministic step</span>
		</div>
		<Handle type="source" position={Position.Bottom} />
	</div>
)

const NoteNode = ({ data }) => (
	<div className="ai-flow-node ai-flow-node--note">
		<Handle type="target" position={Position.Top} />
		<div className="ai-flow-node-icon">
			<StickyNote size={15} />
		</div>
		<div className="ai-flow-node-text">
			<span className="ai-flow-node-title">{data.label}</span>
			{data.description ? (
				<span className="ai-flow-node-kind">{data.description}</span>
			) : (
				<span className="ai-flow-node-kind">Note</span>
			)}
		</div>
		<Handle type="source" position={Position.Bottom} />
	</div>
)

// reactflow requires a stable reference — define the map once at module scope.
export const NODE_TYPES = {
	trigger: TriggerNode,
	agent: AgentNode,
	router: RouterNode,
	step: StepNode,
	note: NoteNode,
}

// Palette + defaults for adding fresh nodes from the canvas toolbar.
export const NODE_PALETTE = [
	{ type: "trigger", label: "Trigger" },
	{ type: "agent", label: "Agent" },
	{ type: "router", label: "Router" },
	{ type: "step", label: "Step" },
	{ type: "note", label: "Note" },
]

export const defaultNodeData = (type) => {
	switch (type) {
		case "agent":
			return {
				label: "New agent",
				binding: "placeholder",
				status: "planned",
				draft: {
					model: "",
					temperature: 0,
					system_instructions: "",
					user_instructions: "",
					json_schema: {},
					json_schema_name: "",
					json_schema_strict: false,
				},
			}
		case "trigger":
			return { label: "Trigger", description: "" }
		case "router":
			return { label: "Router", description: "" }
		case "step":
			return { label: "Step", description: "" }
		case "note":
			return { label: "Note", description: "" }
		default:
			return { label: "Node", description: "" }
	}
}
